import type { CSSProperties } from 'react'
import type { LandingCampaign } from '@/types/landing'

const FALLBACK_PRIMARY = '#1a2744'
const FALLBACK_SECONDARY = '#e8ecf4'
const FALLBACK_BUTTON = '#5e17eb'
const FALLBACK_HIGHLIGHT = '#ff8800'

/**
 * Normaliza string hex (#RGB ou #RRGGBB). Retorna null se inválido.
 */
export function normalizeHexColor(input: string | null | undefined): string | null {
  if (input == null) return null
  const s = input.trim()
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s)) return null
  if (s.length === 4) {
    const r = s[1]
    const g = s[2]
    const b = s[3]
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase()
  }
  return s.toLowerCase()
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const n = normalizeHexColor(hex)
  if (!n || n.length !== 7) return null
  return {
    r: Number.parseInt(n.slice(1, 3), 16),
    g: Number.parseInt(n.slice(3, 5), 16),
    b: Number.parseInt(n.slice(5, 7), 16),
  }
}

function rgbToHex(r: number, g: number, b: number): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v)))
  return `#${c(r).toString(16).padStart(2, '0')}${c(g).toString(16).padStart(2, '0')}${c(b).toString(16).padStart(2, '0')}`
}

export function mixHex(a: string, b: string, t: number): string {
  const A = hexToRgb(a)
  const B = hexToRgb(b)
  if (!A || !B) return a
  return rgbToHex(A.r + (B.r - A.r) * t, A.g + (B.g - A.g) * t, A.b + (B.b - A.b) * t)
}

function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex)
  if (!rgb) return 0
  const linear = [rgb.r, rgb.g, rgb.b].map((c) => {
    const x = c / 255
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2]
}

export function pickContrastTextOnBackground(bgHex: string): '#0a0e18' | '#ffffff' {
  return relativeLuminance(bgHex) > 0.5 ? '#0a0e18' : '#ffffff'
}

/**
 * Variáveis CSS para a página pública do ganhador, alinhadas às cores da campanha (API pública).
 */
export function buildWinnerPageStyleVars(campaign: LandingCampaign): CSSProperties {
  const primary = normalizeHexColor(campaign.primary_color) ?? FALLBACK_PRIMARY
  const secondary = normalizeHexColor(campaign.secondary_color) ?? FALLBACK_SECONDARY
  const button = normalizeHexColor(campaign.button_color) ?? FALLBACK_BUTTON
  const highlight = normalizeHexColor(campaign.highlight_color) ?? FALLBACK_HIGHLIGHT

  const bgDeep = mixHex(primary, '#000000', 0.72)
  const bgMid = mixHex(primary, '#0a0e18', 0.55)
  const bgGlow = mixHex(highlight, primary, 0.35)

  const surface = mixHex(secondary, '#0a0e18', 0.88)
  const surfaceBorder = mixHex(highlight, primary, 0.4)

  const textMuted = mixHex(secondary, '#6b7280', 0.45)
  const fg = mixHex('#f8fafc', secondary, 0.15)
  const kickerEnd = mixHex(highlight, '#fef3c7', 0.25)

  const onButton = pickContrastTextOnBackground(button)
  const brandRing = mixHex(highlight, primary, 0.5)
  const brandChipBg = mixHex(highlight, primary, 0.38)
  const onBrandChip = pickContrastTextOnBackground(brandChipBg)
  const kickerStart = mixHex(highlight, '#ffffff', 0.28)

  return {
    '--winner-primary': primary,
    '--winner-secondary': secondary,
    '--winner-button': button,
    '--winner-highlight': highlight,
    '--winner-bg-deep': bgDeep,
    '--winner-bg-mid': bgMid,
    '--winner-bg-glow': bgGlow,
    '--winner-surface': surface,
    '--winner-surface-border': surfaceBorder,
    '--winner-text-muted': textMuted,
    '--winner-fg': fg,
    '--winner-kicker-start': kickerStart,
    '--winner-kicker-end': kickerEnd,
    '--winner-on-button': onButton,
    '--winner-brand-ring': brandRing,
    '--winner-brand-chip': brandChipBg,
    '--winner-on-brand': onBrandChip,
  } as CSSProperties
}
